import React from 'react';
import { useDispatch } from 'react-redux';
import {
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  MailOutlined,
  PhoneFilled,
  EnvironmentFilled,
  BankFilled,
  SafetyCertificateFilled
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { WEBSITE_URL } from '../Helper';
import { getConsultantProfileData } from '../services/ConsultantService';
import { getCleaningRestorationProfileData } from '../services/CleaningRestorationService';
import './UserProfileCard.css';

const UserProfileCard = ({ profile }) => {
  const dispatch = useDispatch();
  const safeProfile = profile || {};
  const {
    ID,
    email_ins,
    profile_pic,
    logo_url,
    insp_fullname,
    phone_number,
    address,
    organization_name,
    profile_permalink,
    post_type,
    status = 'active', // Default status: 'active', 'expired', or 'canceled'
  } = safeProfile;

  const isTrustedDealer = post_type === 'trusted_dealer';
  const isConsultant = post_type === 'consultant_profile';
  const isCleaningRestoration = post_type === 'cleaning_restoration';

  let editLink = `/inspector-profile/?inspector_id=${ID}`;
  if (isTrustedDealer) {
    editLink = `/trusted-dealer-dashboard/?dealer_id=${ID}`;
  } else if (isConsultant) {
    editLink = `/consultant-profile/?consultant_id=${ID}`;
  } else if (isCleaningRestoration) {
    editLink = `/cleaning-dashboard?profile_id=${ID}`;
  }

  const [avatarImage, setAvatarImage] = React.useState(profile_pic || logo_url || safeProfile?.photo_url || null);

  React.useEffect(() => {
    let img = profile_pic || logo_url || safeProfile?.photo_url;
    if (img) {
      setAvatarImage(img);
    } else if (isConsultant && ID) {
      dispatch(getConsultantProfileData(ID)).then((res) => {
        if (res && (res.profile_pic || res.photo_url)) {
          setAvatarImage(res.profile_pic || res.photo_url);
        }
      }).catch(() => {});
    } else if (isCleaningRestoration && ID) {
      dispatch(getCleaningRestorationProfileData(ID)).then((res) => {
        if (res && (res.profile_pic || res.logo_url)) {
          setAvatarImage(res.profile_pic || res.logo_url);
        }
      }).catch(() => {});
    }
  }, [profile_pic, logo_url, safeProfile?.photo_url, isConsultant, isCleaningRestoration, ID, dispatch]);

  if (!profile) return null;
  const isStatusExpired = status?.toLowerCase() === 'expired';
  const showRenewButton = isStatusExpired;

  const handleRenew = (ID) => {
    if (isConsultant) {
      window.location.href = `${WEBSITE_URL}/product/consultant-addon/?renewal_profile_id=${ID}`;
    } else {
      window.location.href = `${WEBSITE_URL}/product/new-location/?renewal_profile_id=${ID}`; 
    }
  };

  const getRoleTitle = () => {
    if (isConsultant) return 'Flooring Consultant';
    if (isTrustedDealer) return 'Trusted Dealer';
    if (isCleaningRestoration) return 'Cleaning & Restoration';
    if (post_type === 'installer_profile') return 'Installer';
    return 'Inspector';
  };

  const getCardTypeClass = () => {
    if (isConsultant) return 'consultant-card';
    if (isTrustedDealer) return 'dealer-card';
    if (isCleaningRestoration) return 'cleaning-card';
    return 'inspector-card';
  };

  // Function to get status tag configuration
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'expired':
        return { color: 'red', text: 'Expired', class: 'expired' };
      case 'canceled':
      case 'cancelled':
        return { color: 'default', text: 'Canceled', class: 'inactive' };
      case 'inactive':
        return { color: 'default', text: 'Inactive', class: 'inactive' };
      case 'active':
      default:
        return { color: 'green', text: 'Active', class: 'active' };
    }
  };

  const statusConfig = getStatusConfig(status);
  const getStatusText = () => statusConfig.text;
  const statusClass = statusConfig.class;

  return (
    <div className={`user-profile-card ${getCardTypeClass()} ${isStatusExpired ? 'expired-profile' : ''}`}>
      {/* 1. Corner Wave SVG Decoration */}
      <svg className="card-corner-wave" viewBox="0 0 217 119" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0 0H217C185 30 155 75 95 72C48 70 22 95 0 119V0Z"
          fill={isConsultant ? 'url(#wave-grad-light-purple)' : 'url(#wave-grad-light-blue)'}
          opacity="0.35"
        />
        <path
          d="M0 0H165C130 18 110 60 65 56C28 52 14 82 0 105V0Z"
          fill={isConsultant ? 'url(#wave-grad-main-purple)' : 'url(#wave-grad-main-blue)'}
          opacity="0.78"
        />
        <defs>
          <linearGradient id="wave-grad-main-blue" x1="0" y1="0" x2="165" y2="105" gradientUnits="userSpaceOnUse">
            <stop stopColor="#0077e6" />
            <stop offset="1" stopColor="#38bdf8" />
          </linearGradient>
          <linearGradient id="wave-grad-light-blue" x1="0" y1="0" x2="217" y2="119" gradientUnits="userSpaceOnUse">
            <stop stopColor="#0284c7" />
            <stop offset="1" stopColor="#bae6fd" />
          </linearGradient>
          <linearGradient id="wave-grad-main-purple" x1="0" y1="0" x2="165" y2="105" gradientUnits="userSpaceOnUse">
            <stop stopColor="#7c3aed" />
            <stop offset="1" stopColor="#c084fc" />
          </linearGradient>
          <linearGradient id="wave-grad-light-purple" x1="0" y1="0" x2="217" y2="119" gradientUnits="userSpaceOnUse">
            <stop stopColor="#8b5cf6" />
            <stop offset="1" stopColor="#e9d5ff" />
          </linearGradient>
        </defs>
      </svg>

      {/* 2. Top Header Section: Avatar (left), Name & Badges (right) */}
      <div className="wave-card-header">
        <div className="wave-card-identity">
          {/* Circular Avatar with crisp white frame and status dot */}
          <div className="wave-avatar-wrapper">
            {avatarImage ? (
              <img src={avatarImage} alt={insp_fullname || 'Profile'} />
            ) : (
              <svg viewBox="0 0 100 100" className="wave-avatar-inner" xmlns="http://www.w3.org/2000/svg">
                <rect width="100" height="100" fill={isConsultant ? '#c084fc' : '#38bdf8'} />
                <circle cx="50" cy="45" r="24" fill="#fed7aa" />
                {/* Hair */}
                <path d="M26 44C26 30 36 20 50 20C64 20 74 30 74 44C74 45 74 52 72 58C68 70 58 76 50 76C42 76 32 70 28 58C26 52 26 45 26 44Z" fill="#292524" />
                <path d="M32 44C32 32 40 24 50 24C60 24 68 32 68 44C68 52 64 62 50 62C36 62 32 52 32 44Z" fill="#fed7aa" />
                {/* Beard */}
                <path d="M34 50C34 66 42 72 50 72C58 72 66 66 66 50C66 54 62 66 50 66C38 66 34 54 34 50Z" fill="#292524" />
                <path d="M42 54C46 56 54 56 58 54C56 58 44 58 42 54Z" fill="#292524" />
                {/* Eyes */}
                <circle cx="43" cy="42" r="2.2" fill="#1c1917" />
                <circle cx="57" cy="42" r="2.2" fill="#1c1917" />
                {/* Suit & Tie */}
                <path d="M15 100C15 84 30 78 50 78C70 78 85 84 85 100H15Z" fill="#1e293b" />
                <polygon points="50,78 44,92 50,100 56,92" fill={isConsultant ? '#7c3aed' : '#0284c7'} />
                <polygon points="50,78 40,86 46,86" fill="#ffffff" />
                <polygon points="50,78 60,86 54,86" fill="#ffffff" />
              </svg>
            )}
            <span className={`wave-avatar-status-dot ${statusClass}`} title={getStatusText()} />
          </div>

          {/* Name & Badges */}
          <div className="wave-header-titles">
            <div className="wave-profile-name" title={insp_fullname || email_ins || 'Profile'}>
              {insp_fullname || email_ins || 'Profile'}
            </div>
            <div className="wave-badges-row">
              <span className={`wave-badge-role ${isConsultant ? 'consultant' : ''}`}>
                <SafetyCertificateFilled style={{ fontSize: 13 }} />
                <span>{getRoleTitle()}</span>
              </span>
              <span className={`wave-badge-status ${statusClass}`}>
                <span className={`wave-status-dot ${statusClass}`} />
                <span>{getStatusText()}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Middle Section: 2x2 Grid on desktop, Email 100%, Phone 50%, Company 50%, Address 100% on <= 767px */}
      <div className="wave-card-middle">
        <div className="wave-entry-row wave-entry-email" title={email_ins}>
          <div className="wave-entry-icon">
            <MailOutlined />
          </div>
          <div className="wave-entry-texts">
            <span className="wave-entry-label">Email Address</span>
            <span className="wave-entry-val">{email_ins || '—'}</span>
          </div>
        </div>

        <div className="wave-entry-row wave-entry-phone" title={phone_number}>
          <div className="wave-entry-icon">
            <PhoneFilled />
          </div>
          <div className="wave-entry-texts">
            <span className="wave-entry-label">Phone Number</span>
            <span className="wave-entry-val">{phone_number || '—'}</span>
          </div>
        </div>

        <div className="wave-entry-row wave-entry-company" title={organization_name}>
          <div className="wave-entry-icon">
            <BankFilled />
          </div>
          <div className="wave-entry-texts">
            <span className="wave-entry-label">Company</span>
            <span className="wave-entry-val">{organization_name || '—'}</span>
          </div>
        </div>

        <div className="wave-entry-row wave-entry-address" title={address}>
          <div className="wave-entry-icon">
            <EnvironmentFilled />
          </div>
          <div className="wave-entry-texts">
            <span className="wave-entry-label">Address</span>
            <span className="wave-entry-val">{address || '—'}</span>
          </div>
        </div>
      </div>

      {/* 4. Bottom Split Action Buttons: "View Profile" & "Edit Details" */}
      <div className="wave-card-footer">
        {showRenewButton && (
          <button
            type="button"
            onClick={() => handleRenew(ID)}
            className="wave-footer-btn renew-btn"
          >
            <ReloadOutlined /> <span>Renew</span>
          </button>
        )}
        <a
          href={profile_permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="wave-footer-btn view-profile"
        >
          <EyeOutlined /> <span>View Profile</span>
        </a>
        <Link
          to={editLink}
          className="wave-footer-btn edit-details"
        >
          <EditOutlined /> <span>Edit Details</span>
        </Link>
      </div>
    </div>
  );
};

export default UserProfileCard;